import { z } from "zod";
import { customerAudienceSchema } from "./audience.js";
z.config({ jitless: true });
/**
 * One step of a customer's journey, derived from the records the product already writes
 * (journey audit and pilot cockpit, 2026-10-16). It holds ids, codes, counts and money only:
 * never a name, an email, words of a story or any other content.
 */
export const journeyEventTypeSchema = z.enum([
    "ACCOUNT_CREATED",
    "AUDIENCE_CHANGED",
    "BOOK_CREATED",
    "BOOK_DELETION_REQUESTED",
    "BOOK_DELETION_CANCELLED",
    "BOOK_DELETED",
    "VOUCHER_REDEEMED",
    "VOUCHER_REDEMPTION_REJECTED",
    "JOB_QUEUED",
    "JOB_STARTED",
    "JOB_SUCCEEDED",
    "JOB_NEEDS_ATTENTION",
    "JOB_FAILED",
    /** Money a finished job settled after it finished (the difference, never the total). */
    "AI_COST_SETTLED",
    "REVIEW_EXPORT_READY",
    "REVIEW_EXPORT_FAILED",
    "PICTURE_CHECKED",
    "STEP_APPROVED",
    "BUDGET_UPDATED",
    "BOOK_FINISHED",
    "PRINT_APPROVED",
    "EMAIL_SENT",
    "EMAIL_DELIVERED",
    "EMAIL_DELAYED",
    "EMAIL_BOUNCED",
    "EMAIL_COMPLAINED",
    "EMAIL_REJECTED",
    "SUPPORT_REQUEST_RECEIVED",
    "SUPPORT_REQUEST_UPDATED",
    /** A printed copy was ordered (pilot watch, 2026-10-17). */
    "PRINT_ORDERED",
    /** The printed copy moved to a new state: sent, accepted, printed, shipped, delivered... */
    "PRINT_UPDATED",
    /** The customer's page failed: a code and the screen, never a message or a stack. */
    "CLIENT_ERROR",
    /** Requests that failed on the server (5xx) or were throttled (429), counted per window. */
    "API_ERROR",
]);
/** A screen's path with every id replaced by ":id", query and hash dropped. */
export const journeyScreenSchema = z.string().regex(/^\/[a-z0-9/:._-]{0,120}$/u);
/** An API route as API Gateway names it, its parameters still in braces. */
export const journeyRouteKeySchema = z
    .string()
    .regex(/^(?:(?:GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS|ANY) \/[A-Za-z0-9/{}._+-]{0,200}|\$default)$/u);
/**
 * A screen as its route, never what fills it: "/app/books/01M5…/guided?x=1" becomes
 * "/app/books/:id/guided". Only a short lowercase word is kept as it is; an id, a share token,
 * a code or anything else in a segment becomes ":id", and the query and hash are dropped.
 */
export function journeyScreen(path) {
    const word = /^[a-z][a-z-]{0,23}$/u;
    const screen = (path.split(/[?#]/u)[0] ?? "")
        .split("/")
        .map((segment) => (segment === "" || word.test(segment) ? segment : ":id"))
        .join("/");
    return journeyScreenSchema.safeParse(screen).success ? screen : undefined;
}
/** Where a book is in the guided journey, furthest point reached. */
export const journeyStepSchema = z.enum([
    "STARTED",
    "STORY",
    "LOOK",
    "WORDS",
    "COVER",
    "PICTURES",
    "BOOK",
    "PRINT",
]);
export const journeyStepRank = {
    STARTED: 0,
    STORY: 1,
    LOOK: 2,
    WORDS: 3,
    COVER: 4,
    PICTURES: 5,
    BOOK: 6,
    PRINT: 7,
};
const code = z.string().regex(/^[A-Z][A-Z0-9_]{0,95}$/u);
const safeId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u);
const micros = z.number().int().nonnegative().max(1_000_000_000_000);
export const journeyEventSchema = z
    .object({
    schemaVersion: z.literal("2026-10-16.1"),
    /** sha256 of the event's natural key: live capture, redelivery and backfill agree. */
    eventId: z.string().regex(/^[0-9a-f]{32}$/u),
    eventType: journeyEventTypeSchema,
    occurredAt: z.string().datetime(),
    customerId: safeId.optional(),
    bookId: safeId.optional(),
    bookType: z
        .string()
        .regex(/^[a-z][a-z0-9-]{0,47}$/u)
        .optional(),
    step: journeyStepSchema.optional(),
    task: code.optional(),
    jobId: safeId.optional(),
    artifactId: safeId.optional(),
    attempt: z.number().int().nonnegative().max(100).optional(),
    /** A record's own revision (the AI allowance's), so the newest always wins. */
    revision: z.number().int().nonnegative().optional(),
    provider: code.optional(),
    modelId: z
        .string()
        .regex(/^[a-z0-9][a-z0-9._:-]{0,95}$/iu)
        .optional(),
    outcome: z
        .enum(["SUCCEEDED", "NEEDS_ATTENTION", "FAILED", "PASS", "FAIL", "NEEDS_HUMAN"])
        .optional(),
    /** A code from the record (never its text), or UNCLASSIFIED. */
    failureCode: code.optional(),
    durationMs: z
        .number()
        .int()
        .nonnegative()
        .max(30 * 24 * 60 * 60 * 1_000)
        .optional(),
    costMicros: micros.optional(),
    reservedMicros: micros.optional(),
    limitMicros: micros.optional(),
    messageType: z
        .string()
        .regex(/^[a-z][a-z0-9_]{0,47}$/u)
        .optional(),
    ticketId: safeId.optional(),
    ticketState: code.optional(),
    ticketCategory: code.optional(),
    voucherPurpose: code.optional(),
    /** A printed copy's order, its state, and whether it went to a test printer. */
    printOrderId: safeId.optional(),
    printState: code.optional(),
    printTest: z.boolean().optional(),
    routeKey: journeyRouteKeySchema.optional(),
    httpStatus: z.number().int().min(100).max(599).optional(),
    screen: journeyScreenSchema.optional(),
    /** How many times it happened in the window it stands for (API_ERROR). */
    count: z.number().int().positive().max(1_000_000).optional(),
    audience: customerAudienceSchema.optional(),
    /** The record the event was derived from, for tracing; a type name, never a key. */
    source: z.string().regex(/^[A-Za-z][A-Za-z0-9]{0,63}$/u),
})
    .strict();
//# sourceMappingURL=journey-event.js.map